// Today's plan in the Day view (DayPlan.swift, DayPlanning.swift): the free time of the day, and which to-do is planned
// for which part of it, in what order. The plan is kept on the to-dos (`plannedUntil`, the end of the free time, and
// `planOrder`), so every device shows the same one. The web reads no calendar, so its day is one free time, from now
// (or the day's start) to the day's end. Cases shared with the kit: cases/day-plan.json.

import { addDays, at, dayOf, startOfDay } from "./days";
import type { Day } from "./types";
import type { Todo } from "./records";

/** Free time: from now or the end of an event to the next event or the end of the day. Its end names it. */
export interface DayGap {
  start: Date;
  end: Date;
  /** The evening after the last event, when that event runs to the end of the day: not counted as free. */
  isAfterDay?: boolean;
}

/** A day from 07:00 to 21:00 (CalendarSettings in the kit), in minutes after midnight. */
export const DEFAULT_DAY_START = 7 * 60;
export const DEFAULT_DAY_END = 21 * 60;
/** Free time shorter than this is not offered for planning. */
const SHORTEST_GAP_MS = 5 * 60 * 1000;
/** How long before the usual start of the day being up counts as starting it early. */
const EARLY_START_MS = 3 * 3600 * 1000;

export interface DayPlan {
  gaps: DayGap[];
  dayStart: Date;
  dayEnd: Date;
  now: Date;
}

/** What is left of a day without a calendar: one free time from now, or the day's start, to its end. */
export function dayPlan(day: Day, zone: string, now: Date, startMinutes = DEFAULT_DAY_START, endMinutes = DEFAULT_DAY_END): DayPlan {
  const usualStart = at(day, Math.floor(startMinutes / 60), startMinutes % 60, zone);
  // An end before the start would leave no day: then the day ends an hour after it starts.
  const end = Math.max(endMinutes, startMinutes + 60);
  const dayEnd = at(day, Math.floor(end / 60), end % 60, zone);
  // Up before the day's usual start: the day starts now, in the hours before it only.
  const startsEarly = now < usualStart && usualStart.getTime() - now.getTime() <= EARLY_START_MS && dayOf(now, zone) === day;
  const dayStart = startsEarly ? now : usualStart;
  const cursor = new Date(Math.max(now.getTime(), dayStart.getTime()));
  const gaps = dayEnd.getTime() - cursor.getTime() >= SHORTEST_GAP_MS ? [{ start: cursor, end: dayEnd }] : [];
  return { gaps, dayStart, dayEnd, now };
}

/**
 * A gap's minutes as the times on screen count them: the clock's minutes, without their seconds (the kit's
 * `wholeMinutes`), so a gap from 18:20 to an event at 18:34:40 is 14 minutes, as the two times say.
 */
export function gapMinutes(gap: Pick<DayGap, "start" | "end">): number {
  const minute = (date: Date) => Math.floor(date.getTime() / 60000);
  return Math.max(0, minute(gap.end) - minute(gap.start));
}

/** The free time that is left, in minutes. */
export function freeMinutes(plan: DayPlan): number {
  return plan.gaps.filter((gap) => !gap.isAfterDay).reduce((sum, gap) => sum + gapMinutes(gap), 0);
}

/** How much of the day has passed, from 0 to 1. */
export function elapsed(plan: DayPlan): number {
  const length = plan.dayEnd.getTime() - plan.dayStart.getTime();
  if (length <= 0) return 0;
  return Math.min(Math.max((plan.now.getTime() - plan.dayStart.getTime()) / length, 0), 1);
}

/** The gap the user is in right now. */
export function currentGap(plan: DayPlan): DayGap | undefined {
  return plan.gaps.find((gap) => gap.start <= plan.now && plan.now < gap.end);
}

/** The end of the free time a to-do is planned for, if it is a plan of today (the evening runs to midnight). */
function plannedEnd(todo: Todo, today: Day, zone: string): Date | undefined {
  const until = todo.plannedUntil;
  if (!until) return undefined;
  const start = startOfDay(today, zone);
  const end = startOfDay(addDays(today, 1), zone);
  return until > start && until <= end ? until : undefined;
}

/** The free time a to-do is planned for, among those left: where its planned moment still lies in free time. */
export function gapOf(todo: Todo, gaps: readonly DayGap[], today: Day, zone: string): DayGap | undefined {
  const end = plannedEnd(todo, today, zone);
  if (!end) return undefined;
  return gaps.find((gap) => gap.start < end && end <= gap.end);
}

/** The to-dos planned for a free time: in the order the user put them, the others after them in the list's order. */
export function plannedIn(gap: DayGap, todos: readonly Todo[], gaps: readonly DayGap[], today: Day, zone: string): Todo[] {
  const here = todos.map((todo, index) => ({ todo, index })).filter(({ todo }) => gapOf(todo, gaps, today, zone) === gap);
  return here.sort((a, b) => {
    const x = a.todo.planOrder, y = b.todo.planOrder;
    if (x !== undefined && y !== undefined && x !== y) return x - y;
    if (x !== undefined && y === undefined) return -1;
    if (x === undefined && y !== undefined) return 1;
    return a.index - b.index;
  }).map(({ todo }) => todo);
}

/** The to-dos planned for no free time that is left. */
export function unplanned(todos: readonly Todo[], gaps: readonly DayGap[], today: Day, zone: string): Todo[] {
  return todos.filter((todo) => gapOf(todo, gaps, today, zone) === undefined);
}

/**
 * Plans to-dos for a free time, before `before` among those planned for it, or after them (Store.plan): every to-do
 * of that free time gets its place. Returns the to-dos that change, to save.
 */
export function planTodos(ids: readonly string[], gap: DayGap, before: string | undefined, list: readonly Todo[], gaps: readonly DayGap[], today: Day, zone: string): Todo[] {
  const byID = new Map(list.map((todo) => [todo.id, todo]));
  const moving = ids.filter((id) => byID.has(id));
  if (moving.length === 0) return [];
  const current = plannedIn(gap, list, gaps, today, zone).map((todo) => todo.id);
  // Dropped on one of those that move, they stay where that one is: before what follows it.
  let anchor = before;
  if (before !== undefined && moving.includes(before)) {
    const at = current.indexOf(before);
    if (at >= 0) anchor = current.slice(at + 1).find((id) => !moving.includes(id));
  }
  const order = current.filter((id) => !moving.includes(id));
  const index = anchor !== undefined && order.includes(anchor) ? order.indexOf(anchor) : order.length;
  order.splice(index, 0, ...moving);
  return order.flatMap((id, place) => {
    const todo = byID.get(id);
    return todo ? [{ ...todo, plannedUntil: gap.end, planOrder: place }] : [];
  });
}

/** Takes to-dos out of today's plan. Returns the to-dos that change. */
export function unplanTodos(ids: readonly string[], list: readonly Todo[]): Todo[] {
  return list.filter((todo) => ids.includes(todo.id) && (todo.plannedUntil !== undefined || todo.planOrder !== undefined)).map((todo) => {
    const changed = { ...todo };
    delete changed.plannedUntil;
    delete changed.planOrder;
    return changed;
  });
}
